import * as React from 'react';
import * as ReactDOM from 'react-dom';

interface TooltipForEllipsisProps {
  children: React.ReactElement;
  tooltipText?: string;
  className?: string;
  isProgrammaticallyVisible?: boolean;
  assignTabIndex?: boolean;
}

const tooltipActualStyleBase: React.CSSProperties = {
  pointerEvents: 'none',
  marginBottom: '0.5rem',
  padding: '0.25rem 0.5rem',
  fontSize: '0.875rem',
  fontWeight: 400,
  lineHeight: '1.5rem',
  color: '#ffffff',
  backgroundColor: '#323232',
  borderRadius: '4px',
  wordWrap: 'break-word',
  zIndex: 9999,
  textAlign: 'center',
};

const tooltipArrowStyleBase: React.CSSProperties = {
  position: 'absolute',
  width: 0,
  height: 0,
  borderLeft: '5px solid transparent',
  borderRight: '5px solid transparent',
};

export const TooltipForEllipsis = ({
  children,
  tooltipText,
  className,
  isProgrammaticallyVisible = false,
  assignTabIndex = false,
}: TooltipForEllipsisProps) => {
  const [isActuallyTruncated, setIsActuallyTruncated] = React.useState(false);
  const [isFocusedByInteraction, setIsFocusedByInteraction] = React.useState(false);
  const [isHoveredByMouse, setIsHoveredByMouse] = React.useState(false);
  const [clickedToHide, setClickedToHide] = React.useState(false);

  const childRef = React.useRef<HTMLElement>(null);
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = React.useState({
    top: 0,
    left: 0,
    visibility: 'hidden' as React.CSSProperties['visibility'],
    width: '320px',
  });
  const [arrowStyle, setArrowStyle] = React.useState<React.CSSProperties>({});

  const isTooltipEffectivelyVisible = React.useMemo(() => {
    if (clickedToHide) return false;
    if (!isActuallyTruncated) return false;
    if (isHoveredByMouse) return true;
    return isFocusedByInteraction || isProgrammaticallyVisible;
  }, [
    clickedToHide,
    isActuallyTruncated,
    isHoveredByMouse,
    isFocusedByInteraction,
    isProgrammaticallyVisible,
  ]);

  const checkTruncation = React.useCallback(() => {
    const element = childRef.current;
    if (element) {
      const currentlyTruncated =
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;
      setIsActuallyTruncated(currentlyTruncated);
      return currentlyTruncated;
    }
    setIsActuallyTruncated(false);
    return false;
  }, []);

  React.useEffect(() => {
    checkTruncation();
    const observer = new ResizeObserver(checkTruncation);
    const currentChildRef = childRef.current;
    if (currentChildRef) {
      observer.observe(currentChildRef);
    }
    window.addEventListener('resize', checkTruncation);
    return () => {
      if (currentChildRef) {
        observer.unobserve(currentChildRef);
      }
      window.removeEventListener('resize', checkTruncation);
    };
  }, [children, checkTruncation]);

  React.useEffect(() => {
    if (isProgrammaticallyVisible && !clickedToHide) {
      checkTruncation();
    }
  }, [isProgrammaticallyVisible, clickedToHide, checkTruncation]);

  React.useLayoutEffect(() => {
    if (
      isActuallyTruncated &&
      isTooltipEffectivelyVisible &&
      childRef.current &&
      tooltipRef.current
    ) {
      const triggerRect = childRef.current.getBoundingClientRect();
      const tooltipNode = tooltipRef.current;

      let availableWidth = window.innerWidth - window.innerWidth * 0.25;
      const contentLength = childRef.current ? childRef.current.clientWidth : 0;
      if (contentLength && contentLength < availableWidth) {
        availableWidth = contentLength;
      }
      if (availableWidth > 320) {
        availableWidth = 320;
      }

      tooltipNode.style.maxWidth = `${availableWidth}px`;

      const tooltipRect = tooltipNode.getBoundingClientRect();

      let newTop = triggerRect.top - tooltipRect.height - 8;
      let newLeft = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

      const arrowHeight = 5;
      let dynamicArrowStyle: React.CSSProperties = {
        ...tooltipArrowStyleBase,
        left: '50%',
        transform: 'translateX(-50%)',
        borderTop: `5px solid ${tooltipActualStyleBase.backgroundColor || '#323232'}`,
        top: '100%',
      };

      if (newLeft < 5) newLeft = 5;
      if (newLeft + tooltipRect.width > window.innerWidth - 5) {
        newLeft = window.innerWidth - tooltipRect.width - 5;
      }
      if (newTop < 5) {
        newTop = triggerRect.bottom + arrowHeight + 8;
        dynamicArrowStyle = {
          ...tooltipArrowStyleBase,
          left: '50%',
          transform: 'translateX(-50%)',
          borderBottom: `5px solid ${tooltipActualStyleBase.backgroundColor || '#323232'}`,
          top: `-${arrowHeight}px`,
        };
      }

      const arrowLeftRelativeToTooltip =
        triggerRect.left + triggerRect.width / 2 - newLeft - arrowHeight;
      dynamicArrowStyle.left = `${Math.max(
        arrowHeight,
        Math.min(arrowLeftRelativeToTooltip, tooltipRect.width - arrowHeight),
      )}px`;
      dynamicArrowStyle.transform = 'translateX(0%)';

      setArrowStyle(dynamicArrowStyle);
      setTooltipPosition({
        top: newTop,
        left: newLeft,
        visibility: 'visible',
        width: `${availableWidth}px`,
      });
    } else {
      setTooltipPosition((prev) => ({ ...prev, visibility: 'hidden' }));
    }
  }, [isTooltipEffectivelyVisible, isActuallyTruncated, children, tooltipText]);

  const singleChild = React.Children.only(children);

  const handleFocus = (event?: React.FocusEvent<HTMLElement>) => {
    checkTruncation();
    setClickedToHide(false);
    setIsFocusedByInteraction(true);

    if (event && typeof singleChild.props.onFocus === 'function') {
      singleChild.props.onFocus(event);
    }
  };

  const handleBlur = (event?: React.FocusEvent<HTMLElement>) => {
    setClickedToHide(false);
    setIsFocusedByInteraction(false);
    if (event && typeof singleChild.props.onBlur === 'function') {
      singleChild.props.onBlur(event);
    }
  };

  const handleMouseEnter = (event?: React.MouseEvent<HTMLElement>) => {
    checkTruncation();
    setClickedToHide(false);
    setIsHoveredByMouse(true);

    if (event && typeof singleChild.props.onMouseEnter === 'function') {
      singleChild.props.onMouseEnter(event);
    }
  };

  const handleMouseLeave = (event?: React.MouseEvent<HTMLElement>) => {
    setIsHoveredByMouse(false);

    if (event && typeof singleChild.props.onMouseLeave === 'function') {
      singleChild.props.onMouseLeave(event);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setClickedToHide(true);

    if (typeof singleChild.props.onClick === 'function') {
      singleChild.props.onClick(event);
    }
  };

  const textForTooltip = tooltipText || (childRef.current ? childRef.current.textContent : '');
  const tooltipId = React.useMemo(
    () => `tooltip-${Math.random().toString(36).substring(2, 9)}`,
    [],
  );

  const childWithRefAndFocus = React.cloneElement(singleChild as React.ReactElement, {
    ref: childRef,
    title: '',
    tabIndex: assignTabIndex ? 0 : singleChild.props.tabIndex,
    onFocus: (e: React.FocusEvent<HTMLElement>) => handleFocus(e),
    onBlur: (e: React.FocusEvent<HTMLElement>) => handleBlur(e),
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => handleMouseEnter(e),
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => handleMouseLeave(e),
    onClick: handleClick, // Add the custom click handler
    'aria-describedby': isTooltipEffectivelyVisible ? tooltipId : undefined,
  });

  const tooltipContainerStyle: React.CSSProperties = {
    position: 'relative',
    display: 'block',
    width: '100%',
    minWidth: '0',
  };

  const tooltipJsx = (
    <div
      id={tooltipId}
      role="tooltip"
      ref={tooltipRef}
      style={{
        ...tooltipActualStyleBase,
        position: 'fixed',
        top: `${tooltipPosition.top}px`,
        left: `${tooltipPosition.left}px`,
        visibility: tooltipPosition.visibility,
        width: tooltipPosition.width,
        transform: 'none',
        bottom: 'auto',
      }}
    >
      {textForTooltip}
      <div style={arrowStyle}></div>
    </div>
  );

  return (
    <div className={className} style={tooltipContainerStyle}>
      {childWithRefAndFocus}
      {isTooltipEffectivelyVisible &&
        typeof document !== 'undefined' &&
        document.body &&
        ReactDOM.createPortal(tooltipJsx, document.body)}
    </div>
  );
};
